/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { updateLineDeliverySpecificationsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';

import getRetailStoreAddressAction, { GetRetailStoreAddressInput } from '../actions/get-retail-store-address.action';
import { getSelectedStoreId } from './cookieutils';

/** Store pickup, which the channel's address types name 6. */
const STORE_ADDRESS_TYPE = 6;

/**
 * Gives the cart's lines that have no delivery mode the store's own, from which the channel works out their tax.
 * @param context - The page's context.
 * @returns True when every line has a delivery mode afterwards.
 */
export const applyLineDeliveryContext = async (context: ICoreContext<IGeneric<IAny>>): Promise<boolean> => {
    const cartState = await getCartState(context.actionContext);
    const cart = cartState.cart;
    const missing = (cart?.CartLines ?? []).filter(line => !line.DeliveryMode);
    if (!cart?.Id || missing.length === 0) {
        return true;
    }

    const channelId = parseInt(getSelectedStoreId() || '', 10);
    if (!Number.isFinite(channelId) || channelId <= 0) {
        return false;
    }

    try {
        const store = await getRetailStoreAddressAction(new GetRetailStoreAddressInput(channelId), context.actionContext);
        if (!store) {
            console.log('LTS Info', 'linedeliverycontext: no store address, so the lines stay untaxed', { lineCount: missing.length });

            return false;
        }

        const deliveryModeId = (context.app.config.deliveryModeId as string | undefined) || 'Carryout';
        const address = {
            Name: store.Name,
            Street: store.Street,
            City: store.City,
            ThreeLetterISORegionName: store.ThreeLetterISORegionName,
            State: store.State,
            ZipCode: store.ZipCode,
            Phone: store.Phone,
            AddressTypeValue: STORE_ADDRESS_TYPE
        };

        await updateLineDeliverySpecificationsAsync(
            { callerContext: context.actionContext },
            cart.Id,
            missing.map(line => ({
                LineId: line.LineId,
                DeliverySpecification: { DeliveryModeId: deliveryModeId, DeliveryPreferenceTypeValue: 1, DeliveryAddress: address }
            }))
        );
        await cartState.refreshCart({});

        return true;
    } catch (error) {
        console.log('LTS Info', 'linedeliverycontext: the delivery context was not applied', error);

        return false;
    }
};
